import { MantineProvider } from '@mantine/core';
import { CssBaseline, CssVarsProvider } from '@mui/joy';
import { THEME_ID, ThemeProvider } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { MutationCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import React from 'react';
import ReactDOM from 'react-dom/client';

import 'maplibre-gl/dist/maplibre-gl.css';
import '@mantine/core/styles.css';

import '@/setup/sentry';
import { App } from '@/App';
import { AbilityProvider } from '@/hooks/useAppAbility';
import { client } from '@/modules/api/client.gen';
import '@/setup/dayjs';
import { mantineTheme } from '@/setup/mantineTheme';
import { materialTheme, theme } from '@/setup/theme';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
    },
  },
  mutationCache: new MutationCache({
    onSuccess: async (_data, _vars, _ctx, mutation) => {
      if (mutation.meta?.invalidateQueries === false) return;
      await queryClient.invalidateQueries();
    },
  }),
});

client.setConfig({
  baseUrl: import.meta.env.VITE_API_ORIGIN,
});

const root = ReactDOM.createRoot(document.getElementById('root')!);
root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <AbilityProvider>
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="de">
          <MantineProvider theme={mantineTheme}>
            <ThemeProvider theme={{ [THEME_ID]: materialTheme }}>
              <CssVarsProvider theme={theme}>
                <CssBaseline />
                <App />
              </CssVarsProvider>
            </ThemeProvider>
          </MantineProvider>
        </LocalizationProvider>
      </AbilityProvider>
    </QueryClientProvider>
  </React.StrictMode>,
);
